import { Component, output, inject, effect, computed, signal } from '@angular/core';
import { CurrentStatus } from 'src/app/models/currentStatus';
import { MatDialogModule, MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { BulkErrorUpdateScope } from 'src/app/models/bulk-error-update-scope';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

interface DialogData {
  selectedErrorIds: string[];
  statuses: CurrentStatus[];
}

@Component({
  selector: 'app-lt-error-bulk-update-dialog',
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatButtonToggleModule,
    MatSelectModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
  ],
  templateUrl: './lt-error-bulk-update-dialog.html',
  styleUrl: './lt-error-bulk-update-dialog.scss',
})
export class LTErrorBulkUpdateDialogComponent {
  private readonly dialog = inject(MatDialog);
  private readonly dialogRef = inject(MatDialogRef<LTErrorBulkUpdateDialogComponent>);
  
  bulkUpdate = output<BulkErrorUpdateScope>();
  dialogClosed = output<void>();

  // Inject dialog data instead of using input signals for dialog components
  private readonly data = inject<DialogData>(MAT_DIALOG_DATA);

  // Convert injected data to signals for reactive behavior
  selectedErrorIds = signal(this.data?.selectedErrorIds || []);
  statuses = signal(this.data?.statuses || []);
  
  //#region Internal signals
  private _bulkUpdateSelectedStatus = signal<CurrentStatus | null>(null);
  private _criticalError = signal<boolean | null>(null);
  private _actionNeeded = signal<boolean | null>(null);
  private _automatedResolution = signal<boolean | null>(null);
  private _jiraStoryURL = signal('');
  private _resolutionComment = signal('');
  isUpdating = signal(false);
  //#endregion
  
  //#region Public computed signals
  bulkUpdateSelectedStatus = computed(() => this._bulkUpdateSelectedStatus());
  criticalError = computed(() => this._criticalError());
  actionNeeded = computed(() => this._actionNeeded());
  automatedResolution = computed(() => this._automatedResolution());
  jiraStoryURL = computed(() => this._jiraStoryURL());
  resolutionComment = computed(() => this._resolutionComment());
  //#endregion

  //#region Computed validation signals
  isStatusResolved = computed(() => {
    const status = this.bulkUpdateSelectedStatus();
    return status?.statusName === 'Resolved';
  });

  canUpdate = computed(() => {
    const status = this.bulkUpdateSelectedStatus();
    
    // Must have a status selected
    if (!status) {
      return false;
    }
    
    // If status is Resolved, must have resolution comment
    if (this.isStatusResolved()) {
      return !!(this.resolutionComment().trim());
    }
    
    return true;
  });
  //#endregion

  // Computed bulk update details
  bulkUpdateDetails = computed((): BulkErrorUpdateScope => ({
    integrationErrorIdList: this.selectedErrorIds(),
    currentStatusId: this.bulkUpdateSelectedStatus()?.currentStatusId,
    statusName: this.bulkUpdateSelectedStatus()?.statusName,
    criticalError: this.criticalError(),
    actionNeeded: this.actionNeeded(),
    automatedResolution: this.automatedResolution(),
    jiraStoryURL: this.jiraStoryURL() || undefined,
    resolutionComment: this.resolutionComment() || undefined,
    resolutionDate: this.isStatusResolved() ? new Date() : undefined,
  }));

  constructor() {
    // Effect to clear resolution comment when status changes away from Resolved
    effect(() => {
      if (!this.isStatusResolved()) {
        this._resolutionComment.set('');
      }
    });
  }

  //#region Setter methods
  setCriticalError(value: string | null) {
    this._criticalError.set(value === 'true' ? true : value === 'false' ? false : null);
  }

  setActionNeeded(value: string | null) {
    this._actionNeeded.set(value === 'true' ? true : value === 'false' ? false : null);
  }

  setAutomatedResolution(value: string | null) {
    this._automatedResolution.set(value === 'true' ? true : value === 'false' ? false : null);
  }

  setJiraStoryURL(event: Event) {
    const target = event.target as HTMLInputElement;
    this._jiraStoryURL.set(target.value);
  }

  setResolutionComment(event: Event) {
    const target = event.target as HTMLInputElement;
    this._resolutionComment.set(target.value);
  }
  //#endregion

  updateBulkStatus(event: any) {
    this._bulkUpdateSelectedStatus.set(event.value);
  }

  performBulkUpdate() {
    if (!this.canUpdate()) {
      return;
    }

    this.isUpdating.set(true);

    // Emit the bulk update event with computed details
    this.bulkUpdate.emit(this.bulkUpdateDetails());

    // Close dialog after a brief delay
    setTimeout(() => {
      this.isUpdating.set(false);
      this.closeDialog();
    }, 500);
  }

  closeDialog() {
    this.dialogClosed.emit();
    this.dialogRef?.close();
  }

  // Reset form to initial state
  resetForm() {
    this._bulkUpdateSelectedStatus.set(null);
    this._criticalError.set(null);
    this._actionNeeded.set(null);
    this._automatedResolution.set(null);
    this._jiraStoryURL.set('');
    this._resolutionComment.set('');
  }

  // Static method to open the dialog
  static open(
    dialog: MatDialog,
    selectedErrorIds: string[],
    statuses: CurrentStatus[],
  ): MatDialogRef<LTErrorBulkUpdateDialogComponent> {
    const dialogRef = dialog.open(LTErrorBulkUpdateDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      disableClose: false,
      autoFocus: true,
      data: {
        selectedErrorIds,
        statuses,
      } as DialogData,
    });

    return dialogRef;
  }
}
